import { FC } from "react";
import Group from "@/components/atoms/Group";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Text from "@/components/atoms/Text";
import styles from "./styles.module.css";
import { ArchivedDocumentWithDetails, ContractAttachment, DocumentBase64, FileType } from "@/types";
import { convertBytesToMb, createURLFromBase64, getFileSizeInBytes, getMimeType } from "@/utils/FileUtils";
import Stack from "@/components/atoms/Stack";
import { Skeleton } from "@mantine/core";
import { t } from "i18next";

interface DocumentListProps {
  files: File[] | ArchivedDocumentWithDetails[] | ContractAttachment[] | DocumentBase64[] | null;
  onRemoveFile?: (fileName: string) => void;
  origin: "api" | "local";
  disabled?: boolean;
  isLoading?: boolean;
  isError?: boolean;
}

const DocumentList: FC<DocumentListProps> = ({
  files,
  onRemoveFile,
  origin: _origin,
  disabled = false,
  isLoading = false,
  isError,
}) => {
  if (isLoading) {
    return (
      <Stack>
        <Skeleton height={20} width={"80%"} radius="xs" />
        <Skeleton height={20} width={"90%"} radius="xs" />
      </Stack>
    );
  }

  if (isError) {
    return <Text color="error">Error loading documents</Text>;
  }

  const removeFile = (fileName: string) => {
    if (onRemoveFile) onRemoveFile(fileName);
  };

  if (!files || files.length === 0) {
    return <></>;
  }

  function isFile(item: File | ArchivedDocumentWithDetails | ContractAttachment | DocumentBase64): item is File {
    return item instanceof File;
  }

  function isDocumentBase64(
    item: File | ArchivedDocumentWithDetails | ContractAttachment | DocumentBase64
  ): item is DocumentBase64 {
    return "url" in item && "base64" in item;
  }

  function isContractAttachment(
    item: File | ArchivedDocumentWithDetails | ContractAttachment | DocumentBase64
  ): item is ContractAttachment {
    return "content" in item;
  }

  function isArchivedDocument(
    item: File | ArchivedDocumentWithDetails | ContractAttachment | DocumentBase64
  ): item is ArchivedDocumentWithDetails {
    return "document" in item;
  }

  const getFileInfo = (file: File | ArchivedDocumentWithDetails | ContractAttachment | DocumentBase64) => {
    if (isFile(file)) {
      return {
        name: file.name,
        fileType: file.name.split(".").pop() || "",
        url: URL.createObjectURL(file),
        size: file.size,
        removeId: file.name,
      };
    } else if (isDocumentBase64(file)) {
      if (!file.error) {
        return {
          name: file.id,
          fileType: file.contentType?.split("/")[1] || "",
          url: file.base64
            ? createURLFromBase64(
                file.base64,
                file.contentType || getMimeType(file.contentType?.split("/")[1] as FileType)
              )
            : "",
          size: file.base64 ? getFileSizeInBytes(file.base64) : 0,
          removeId: file.id,
        };
      } else {
        return {
          name: "Unknown file",
          fileType: "pdf",
          url: "#",
          size: 0,
          removeId: "",
        };
      }
    } else if (isContractAttachment(file)) {
      return {
        name: file.fileName,
        fileType: file.fileType,
        url: createURLFromBase64(file.content, getMimeType(file.fileType)),
        size: getFileSizeInBytes(file.content),
        removeId: file.id,
      };
    } else if (isArchivedDocument(file)) {
      return {
        name: file.documentTitle ?? file.fileName,
        fileType: file.fileType,
        url: createURLFromBase64(file.document, getMimeType(file.fileType)),
        size: getFileSizeInBytes(file.document),
        removeId: file.id,
      };
    }

    return {
      name: "",
      fileType: "",
      url: "",
      size: 0,
      removeId: "",
    };
  };

  return (
    <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
      {files.map((file, index: number) => {
        const fileInfo = getFileInfo(file);
        const translatedFileType = translateFileType(fileInfo.fileType.toLowerCase());

        return (
          <li key={index} className={styles.filePreview}>
            <Group justify="space-between">
              <Group>
                {translatedFileType && <SVGAsset name={translatedFileType} />}
                {fileInfo.url ? (
                  <a href={fileInfo.url} target="_blank" rel="noopener noreferrer">
                    <Text size="xs">{fileInfo.name}</Text>
                  </a>
                ) : (
                  <Text size="xs">{fileInfo.name ?? t("notifications:unknownFile")}</Text>
                )}
              </Group>
              <Group gap={"20px"}>
                <label className={styles.fileSize}>
                  <Text size="xs">{convertBytesToMb(fileInfo.size)} Mb</Text>
                </label>
                {!disabled && onRemoveFile && (
                  <i onClick={() => removeFile(fileInfo.removeId)} className={styles.deleteButton}>
                    <SVGAsset name="trashcan" />
                  </i>
                )}
              </Group>
            </Group>
          </li>
        );
      })}
    </ul>
  );
};

export default DocumentList;

function translateFileType(fileType: string): string {
  switch (fileType) {
    case "csv":
      return "excel";
    case "docx":
      return "word";
    case "pdf":
      return "pdf";
    case "jpg":
    case "jpeg":
    case "png":
      return "image";
    case "octet-stream":
      return "pdf";
    default:
      return "defaultFile";
  }
}
